import { AnimalType } from './animal';
import { PageableResult } from './common';
import {
  HealthSystemDomainFarmsResultFarmViewModel,
  HealthSystemDomainLocationsResultLocationViewModel
} from '../api/HealthSystemApi';

export interface LocationViewModel {
  id: string;
  farmId: string;
  farm: HealthSystemDomainFarmsResultFarmViewModel;
  number: string;
  animalType: AnimalType;
  room: string;
  pen: string;
  positioning: LocationPosition;
  stable: LocationStable;
  isSoftDeleted: boolean;
  cycleStartDate?: Date;
  averageAge?: number;
  animalsCount?: number;
  area?: LocationAreaViewModel;
  metadata?: LocationMetadata;
  isAreaCompliant: boolean;
  /** Whether a sensor device is bound to this pen - i.e. whether it has climate readings at all. */
  hasSensorDevice: boolean;
  /** The bound device, or null when `hasSensorDevice` is false. */
  sensorDevice?: LocationSensorDevice | null;
}

/**
 * The sensor device bound to this pen, as the location payload carries it.
 *
 * Deliberately thinner than `SensorDevice` in `sensor-devices.ts`: the locations screens are open to all four
 * roles, and the sensor-device endpoints are Admin + Manager only, so this is what a farm user or veterinarian
 * is allowed to know - which device is measuring this pen, and when it was last heard from.
 *
 * The generated type declares this field non-nullable, but the API sends `null` for an unbound pen. Same as
 * `area` and `metadata`, both of which every call site already guards with `?.`.
 */
export interface LocationSensorDevice {
  id: string;
  /** The device's name at the provider - free text the farmer typed there. */
  name: string;
  measuredTags: string[];
  lastSeenAt?: string | null;
}

/**
 * @deprecated Never drove anything, and drives nothing now that a device is bound to a pen through the
 * Sensor devices screen. The API still returns it; no screen reads it. Use `sensorDevice` instead.
 */
export interface LocationMetadata {
  climateDeviceNumber?: string | null;
  climateProvider?: string | null;
}

export interface LocationAreaViewModel {
  areaInM?: number;
}

export interface LocationsPageableQueryResult extends PageableResult<LocationViewModel> {}

/**
 * A location as offered by `FarmLocationSelectModal`'s picker list and its `location`/`onChange`
 * props. Trimmed to `id`, `number`, `room`, `pen`, `animalType` and `stable` - the fields the modal's
 * list row and label render. `number`, `room` and `pen` mirror the API's nullable fields, unlike the
 * wider `LocationViewModel` above, which claims them non-null.
 */
export interface LocationOption {
  id: string;
  number: string | null;
  room: string | null;
  pen: string | null;
  animalType: AnimalType;
  stable: LocationStable;
}

export const toLocationOption = (dto: HealthSystemDomainLocationsResultLocationViewModel): LocationOption => ({
  id: dto.id,
  number: dto.number ?? null,
  room: dto.room ?? null,
  pen: dto.pen ?? null,
  animalType: dto.animalType,
  stable: dto.stable
});

export interface LocationsHierarchyResult {
  stables: LocationStableGroup[];
}

export interface LocationStableGroup {
  stable: LocationStable;
  rooms: LocationRoomGroup[];
}

export interface LocationRoomGroup {
  room: string | null;
  locations: LocationViewModel[];
}

export enum LocationPosition {
  Left = 10,
  Right = 20
}

export enum LocationStable {
  /// <summary>
  /// Represents pigs in the fattening stage, where they are being prepared for slaughter.
  /// </summary>
  Fatting = 10,

  /// <summary>
  /// Represents young pigs that are being raised until they are ready for breeding or fattening.
  /// </summary>
  Nursery = 20,

  /// <summary>
  /// Represents female pigs that have given birth to piglets and are currently nursing them.
  /// </summary>
  Farrowing = 30,

  /// <summary>
  /// Represents pregnant female pigs that are being kept for gestation.
  /// </summary>
  Gestation = 40,

  /// <summary>
  /// Represents female pigs that are ready for breeding.
  /// </summary>
  Mating = 50,

  /// <summary>
  /// Represents pigs that are in quarantine for health checks or observation.
  /// </summary>
  Quarantaine = 60,

  /// <summary>
  /// Represents pigs that are in a calm state, often used for observation or rest.
  /// </summary>
  Calm = 70,

  /// <summary>
  /// A female pig that has not produced a litter of piglets yet
  /// or is being used to grow out and finish to butcher.
  /// </summary>
  Gilts = 80,

  /// <summary>
  /// Represents pigs kept for breeding purposes.
  /// </summary>
  Breeding = 90,

  /// <summary>
  /// Represents an unknown or undefined location state.
  /// </summary>
  Unknown = 0
}

export const LocationStableTranslations: Record<LocationStable, string> = {
  [LocationStable.Fatting]: 'f-loc-stable-fat',
  [LocationStable.Nursery]: 'f-loc-stable-nur',
  [LocationStable.Farrowing]: 'f-loc-stable-far',
  [LocationStable.Gestation]: 'f-loc-stable-ges',
  [LocationStable.Mating]: 'f-loc-stable-mat',
  [LocationStable.Calm]: 'f-loc-stable-clm',
  [LocationStable.Gilts]: 'f-loc-stable-gil',
  [LocationStable.Quarantaine]: 'f-loc-stable-qua',
  [LocationStable.Breeding]: 'f-loc-stable-bre',
  [LocationStable.Unknown]: 'f-loc-stable-unk'
};

export interface LocationAnimalsResult {
  locationId: string;
  room: string;
  animals: LocationAnimal[];
}

export interface LocationAnimal {
  id: string;
  number: string;
  animalType: AnimalType;
  dateOfBirth: string;
}
